import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { removeCookieLPlus } from "../utils/leadsterplus.utils";

const WIDGET_SCRIPT_SRC =
  process.env.REACT_APP_WIDGET_LEADSTERPLUS_SRC ??
  "https://dev-leadsterplus-cp-widget.informamarkets.com.br/widget/leadster-plus-widget.latest.js";

// nome da variável global que será criada pelo script
const WIDGET_GLOBAL = "LeadsterPlusWidget";

const LANGUAGE_MAP = {
  pt: "pt-BR",
  en: "en-GB",
};

/**
 * Carrega o script do widget Leadster de forma assíncrona.
 * Verifica se o script já foi carregado antes de tentar carregar novamente.
 *
 * @returns {Promise} Promise que resolve com a instância do widget
 */
const loadWidgetScript = () => {
  // se o widget já está disponível globalmente, verifica se é válido
  if (window[WIDGET_GLOBAL]) {
    const widget = window[WIDGET_GLOBAL];
    // verifica se o widget tem os métodos necessários
    if (
      typeof widget.mount === "function" &&
      typeof widget.unmount === "function"
    ) {
      return Promise.resolve(widget);
    } else {
      // Widget está corrompido, remove para forçar reload
      delete window[WIDGET_GLOBAL];
    }
  }

  // verifica se o script já está no DOM
  const existingScript = document.querySelector(
    `script[src="${WIDGET_SCRIPT_SRC}"]`,
  );

  // se o script existe mas ainda não carregou ou está corrompido, remove e recarrega
  if (existingScript && !window[WIDGET_GLOBAL]) {
    existingScript.remove();
  }

  // se o script existe e está carregando, aguarda o carregamento
  if (existingScript && !existingScript.dataset.loadAttempted) {
    existingScript.dataset.loadAttempted = "true";

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Widget script load timeout"));
      }, 10000); // 10 segundos timeout

      existingScript.addEventListener("load", () => {
        clearTimeout(timeout);
        resolve(window[WIDGET_GLOBAL]);
      });

      existingScript.addEventListener("error", () => {
        clearTimeout(timeout);
        reject(new Error("Widget script failed to load"));
      });
    });
  }

  // se o script ainda não existe, cria e adiciona ao DOM
  const script = document.createElement("script");
  script.src = WIDGET_SCRIPT_SRC;
  script.async = true;
  script.dataset.loadAttempted = "true";

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      script.remove();
      reject(new Error("Widget script load timeout"));
    }, 10000); // 10 segundos timeout

    script.onload = () => {
      clearTimeout(timeout);
      resolve(window[WIDGET_GLOBAL]);
    };

    script.onerror = () => {
      clearTimeout(timeout);
      script.remove();
      reject(new Error("Widget script failed to load"));
    };

    document.body.appendChild(script);
  });
};

/**
 * Componente que renderiza e gerencia o widget Leadster.
 * O widget é montado automaticamente e remontado quando o idioma muda.
 *
 * @param {string} refreshToken - Token de atualização para autenticação do widget
 * @param {Function} onWidgetReady - Callback chamado quando o widget está pronto
 * @param {Function} onWidgetError - Callback chamado quando ocorre erro
 * @param {Function} onWidgetLoadStart - Callback chamado quando o carregamento inicia
 */
export function WidgetSlot({
  refreshToken,
  onWidgetReady,
  onWidgetError,
  onWidgetLoadStart,
}) {
  // ref para o elemento DOM onde o widget será montado
  const containerRef = useRef(null);

  // ref para armazenar a instância do widget
  const widgetRef = useRef(null);

  // ref para armazenar callbacks estáveis
  const callbacksRef = useRef({
    onWidgetReady,
    onWidgetError,
    onWidgetLoadStart,
  });

  // atualiza callbacks sempre que mudarem, mas não dispara re-render
  useEffect(() => {
    callbacksRef.current = { onWidgetReady, onWidgetError, onWidgetLoadStart };
  }, [onWidgetReady, onWidgetError, onWidgetLoadStart]);

  // obtém o código de idioma atual do Redux
  const languageCode = useSelector((state) => state.language.code);

  // converte o código de idioma para o formato esperado pelo widget
  const userLanguage = LANGUAGE_MAP[languageCode] || LANGUAGE_MAP.pt;

  useEffect(() => {
    // flag para evitar atualizações de estado após desmontagem
    let isMounted = true;

    // captura a referência do container no momento da montagem
    const container = containerRef.current;

    if (!container) {
      return;
    }

    callbacksRef.current.onWidgetLoadStart?.();

    // carrega o script e monta o widget
    loadWidgetScript()
      .then((widget) => {
        // verifica se o componente ainda está montado e o container existe
        if (!isMounted || !container) {
          return;
        }

        // verifica se o widget tem o método mount
        if (!widget || typeof widget.mount !== "function") {
          if (isMounted) {
            callbacksRef.current.onWidgetError?.(
              new Error("Widget mount method not available"),
            );
          }
          return;
        }

        // armazena a instância do widget para uso posterior
        widgetRef.current = widget;

        try {
          // monta o widget no container com as configurações de idioma e o refresh token
          widget.mount(container, {
            language: userLanguage,
            refresh_token: refreshToken,
          });

          // notifica o componente pai que o widget está pronto
          if (isMounted) {
            callbacksRef.current.onWidgetReady?.(widget);
          }
        } catch (mountError) {
          if (isMounted) {
            callbacksRef.current.onWidgetError?.(mountError);
          }
        }
      })
      .catch((error) => {
        // Só chama callback se componente ainda está montado
        if (isMounted) {
          callbacksRef.current.onWidgetError?.(error);
        }
      });

    // cleanup: desmonta o widget quando o componente é desmontado ou o idioma muda
    return () => {
      removeCookieLPlus();
      isMounted = false;

      if (widgetRef.current && container) {
        try {
          if (typeof widgetRef.current.unmount === "function") {
            widgetRef.current.unmount(container);
          }
        } catch (unmountError) {
          // erro silencioso ao desmontar
        }
      }

      widgetRef.current = null;
    };
  }, [userLanguage, refreshToken]); // reexecuta quando o idioma ou refresh token mudar

  // renderiza o container onde o widget será montado
  return <div ref={containerRef} />;
}
